/**
 * Media Tag Parser Utility
 *
 * Parses [MEDIA:filename|type=image|placement=hero] tags in LLM responses
 * and replaces them with structured media references.
 */

/**
 * Normalize filename for reliable matching (Phase 3.5)
 *
 * Handles issues like:
 * - "Summer Collection - White  Long gown.jpg" (double space)
 * - "Summer Collection - Bree zy Beach.jpg" (LLM inserted space in "Breezy")
 * - " filename.jpg " (leading/trailing spaces)
 *
 * Strategy: Remove ALL spaces for matching (aggressive but reliable)
 * This handles cases where LLM hallucinates spaces within words.
 *
 * Examples:
 * - "Bree zy Beach.jpg" → "BreezyBeach.jpg"
 * - "White  Long gown.jpg" → "WhiteLonggown.jpg"
 * - "Summer Collection - Photo.jpg" → "SummerCollection-Photo.jpg"
 */
export function normalizeFilename(filename: string): string {
  return filename
    .replace(/\s+/g, '')  // Remove ALL spaces for reliable matching
    .trim()               // Remove any remaining leading/trailing whitespace
    .toLowerCase();       // Case-insensitive matching
}

export interface MediaTag {
  fullMatch: string;
  filename: string;
  type: 'image' | 'video';
  placement: 'hero' | 'inline' | 'background' | 'thumbnail' | 'carousel' | 'story' | 'feed';
  startIndex: number;
  endIndex: number;
}

export interface ParsedContent {
  segments: Array<{
    type: 'text' | 'media';
    content: string;
    mediaTag?: MediaTag;
  }>;
}

/**
 * Regex pattern for media tags:
 * [MEDIA:filename.jpg|type=image|placement=hero]
 * Allows optional whitespace around type=, placement=, and closing bracket for flexibility
 * Supports all placement types: hero, inline, background, thumbnail, carousel, story, feed
 */
const MEDIA_TAG_REGEX = /\[MEDIA:([^\|]+)\|\s*type\s*=\s*(image|video)\|\s*placement\s*=\s*(hero|inline|background|thumbnail|carousel|story|feed)\s*\]/g;

/**
 * Parse text containing media tags and return structured segments
 */
export function parseMediaTags(text: string): ParsedContent {
  // TYPE GUARD: Ensure text is a string to prevent runtime errors
  // This handles cases where undefined, null, or non-string values are passed
  const textString = typeof text === 'string' ? text : String(text || '');

  const segments: ParsedContent['segments'] = [];
  let lastIndex = 0;

  // Find all media tags
  const matches = Array.from(textString.matchAll(MEDIA_TAG_REGEX));

  if (matches.length === 0) {
    // No media tags found, return as plain text
    return {
      segments: [{ type: 'text', content: textString }]
    };
  }

  matches.forEach((match) => {
    const [fullMatch, filename, type, placement] = match;
    const startIndex = match.index!;
    const endIndex = startIndex + fullMatch.length;

    // Normalize filename to handle spacing issues (Phase 3.5)
    // Converts "White  Long" → "White Long", "Bree zy" → "Breezy", etc.
    const normalizedFilename = normalizeFilename(filename);

    // Add text before this tag
    if (startIndex > lastIndex) {
      segments.push({
        type: 'text',
        content: textString.substring(lastIndex, startIndex)
      });
    }

    // Add media tag
    segments.push({
      type: 'media',
      content: fullMatch,
      mediaTag: {
        fullMatch,
        filename: normalizedFilename,  // Use normalized filename for reliable lookups
        type: type as 'image' | 'video',
        placement: placement as MediaTag['placement'],
        startIndex,
        endIndex
      }
    });

    lastIndex = endIndex;
  });

  // Add remaining text after last tag
  if (lastIndex < textString.length) {
    segments.push({
      type: 'text',
      content: textString.substring(lastIndex)
    });
  }

  return { segments };
}

/**
 * Extract all media tags from text
 */
export function extractMediaTags(text: string): MediaTag[] {
  const parsed = parseMediaTags(text);
  return parsed.segments
    .filter(seg => seg.type === 'media')
    .map(seg => seg.mediaTag!);
}

/**
 * Check if text contains media tags
 */
export function hasMediaTags(text: string): boolean {
  return MEDIA_TAG_REGEX.test(text);
}

/**
 * Remove media tags from text (images are displayed separately in mockups)
 */
export function stripMediaTags(text: string): string {
  return text.replace(MEDIA_TAG_REGEX, '').replace(/\s{2,}/g, ' ').trim();
}
